import { useQuery } from "@tanstack/react-query";
import { getCachedIdentity } from "@/offline/identity";
import type { CachedIdentity } from "@/offline/db";

export const offlineIdentityKey = ["offline", "identity"] as const;

/**
 * React Query view of the cached WorkOS identity in IDB. Anchored to a stable
 * key so AuthGuard / sign-out flows can invalidate it after mutating the
 * identity store. Returns `null` when no identity is cached (fresh device,
 * post-signout, or IDB unavailable).
 *
 * - `staleTime: Infinity` — IDB is the source of truth; we only refetch on
 *   explicit invalidation, not on focus / mount churn.
 * - `networkMode: "always"` — overrides the project-wide `offlineFirst`
 *   default because this query reads IDB, not the network. Without it the
 *   first probe would never run while offline.
 * - `retry: false` — IDB failures are surfaced once; nothing to retry.
 */
export function useOfflineIdentity() {
  return useQuery<CachedIdentity | null>({
    queryKey: offlineIdentityKey,
    queryFn: getCachedIdentity,
    staleTime: Infinity,
    retry: false,
    networkMode: "always",
  });
}
